// Sentry initialiser (S30 — Sentry SDK behind the reportError seam).
//
// Safe to import unconditionally: when VITE_SENTRY_DSN is unset (dev,
// CI, demo), initSentry is a no-op so tests don't try to reach
// sentry.io and bundle splitters can keep the SDK in the main chunk
// without runtime cost.
//
// The `error-reporter.ts` resolution chain already prefers the
// global `window.Sentry.captureException` when present; this
// initialiser fills that global by importing @sentry/react and
// calling Sentry.init(). The seam stays unchanged — no consumer
// imports @sentry/react directly.
//
// Tags applied:
//   environment   — VITE_RELEASE-derived (prod | staging | dev)
//   release       — VITE_RELEASE verbatim
//   service       — 'web' (constant, distinguishes from backend
//                   error sources that may share the same project)
//
// Sampling kept conservative until the project has a baseline:
//   tracesSampleRate=0    — no perf monitoring (opt-in later)
//   replaysSessionSampleRate=0
//   replaysOnErrorSampleRate=0

import * as Sentry from '@sentry/react'

import { env } from './env'

let initialised = false

export function initSentry(): void {
  if (initialised) return
  const dsn = env.VITE_SENTRY_DSN?.trim()
  if (!dsn) return

  const release = env.VITE_RELEASE
  const environment = inferEnvironment(release)

  Sentry.init({
    dsn,
    release,
    environment,
    // Off until we have a perf budget. Toggle via a follow-up
    // VITE_ env var when sampling is opted in per environment.
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // Drop bot traffic + the noisy ResizeObserver ones that aren't
    // actionable. Anything below this list bubbles to the dashboard.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
      // React Query's "user-cancelled before fulfilment" is benign
      // in practice — the surface we care about (HTTP failures) is
      // captured by the api/client error path, not the cancel path.
      'AbortError',
    ],
    // Stamp the service tag on every event so a shared Sentry
    // project can fan out by source.
    initialScope: {
      tags: { service: 'web' },
    },
  })

  // Expose the SDK via the existing window.Sentry seam. The
  // error-reporter.ts SentryGlobal contract reads
  // captureException; binding the namespace satisfies that
  // without forcing every call site to import @sentry/react.
  ;(globalThis as { Sentry?: typeof Sentry }).Sentry = Sentry
  initialised = true
}

// inferEnvironment maps a release tag to a Sentry environment
// label. The platform's CI sets VITE_RELEASE to a git SHA in dev
// builds and to a 'staging-<sha>' / 'prod-<sha>' string in
// promoted builds; this strip recovers the original environment
// without an additional env var. Exported for test coverage.
export function inferEnvironment(release: string): string {
  if (release.startsWith('prod-')) return 'production'
  if (release.startsWith('staging-')) return 'staging'
  if (release === 'dev' || release.startsWith('dev-')) return 'development'
  return 'unknown'
}
